import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { H2, SmallText } from '../../components/typography/Typography'
import startupIndia from '../../assets/startupindia.webp'
import icon from '../../assets/logo.svg'
import { Button } from '../../components/ui-blocks/Button'
import { SocialLink } from './SocialLink'
import { Facebook, Insta, LinkedIn, Youtube } from './Icons'
import { Accordion } from '../../components/Accordion'
import { NewsletterSignup } from './NewsletterSignup'
import { ANALYTICS_EVENTS, track } from '../../lib/analytics'

const sectionFooterLinks = [
  {
    sectionName: 'Company',
    links: [
      { label: 'About Us', to: '/' },
      { label: 'Resource Centre', to: '/resource-centre' },
      { label: 'Careers', to: '/careers' },
      { label: 'In the News', to: '/in-the-news' },
      { label: 'Contact Us', to: '/contact' },
      { label: 'Sitemap', to: '/sitemap' },
    ],
  },
  {
    // There is no /job-references index route, so each reference is linked directly.
    sectionName: 'Job References',
    links: [
      { label: 'All Job References', to: '/job-references' },
      { label: 'Republic Day 2026', to: '/job-references/republic-day-2026' },
      { label: 'Independence Day 2026', to: '/job-references/independence-day-2026' },
      { label: 'Republic Day 2023', to: '/job-references/republic-day' },
      { label: 'Orana Conventions', to: '/job-references/orana-conventions' },
      { label: 'Strawberry Fields', to: '/job-references/strawberry-fields' },
    ],
  },
  {
    sectionName: 'Products',
    links: [
      { label: 'Stadium Seating', to: '/stadium-seating' },
      { label: 'Retractable Seating', to: '/retractable-seating' },
      { label: 'Bleachers Seating', to: '/bleachers' },
      { label: 'Sports Flooring', to: '/sports-flooring' },
      { label: 'Facility Equipment', to: '/facility-equipment' },
    ],
  },
  {
    sectionName: 'Legal',
    links: [
      { label: 'Privacy Policy', to: '/privacy-policy' },
      { label: 'Terms of Service', to: '/terms-and-conditions' },
    ],
  }
]

const socials = [
  { platform: 'linkedin', href: 'https://www.linkedin.com/company/xtractpro-seating', icon: <LinkedIn /> },
  { platform: 'instagram', href: 'https://www.instagram.com/xtractproseating?igsh=MWdiYWtwMjI1ZDQ2eg==', icon: <Insta /> },
  { platform: 'youtube', href: 'https://youtube.com/@xtractproseating?si=aZxB-bfGjww6HRW6', icon: <Youtube /> },
  { platform: 'facebook', href: 'https://www.facebook.com/share/1JppMFrhVC/?mibextid=wwXIfr', icon: <Facebook /> },
]

const SocialLinks = () => (
  <div className='flex flex-row gap-4'>
    {socials.map((social) => (
      <SocialLink
        key={social.href}
        href={social.href}
        icon={social.icon}
        onClick={() => track(ANALYTICS_EVENTS.SOCIAL_LINK_CLICKED, { platform: social.platform })}
      />
    ))}
  </div>
)

const ContactInfo = () => (
  <div className='flex flex-col gap-4'>
    <div className='flex flex-col'>
      <SmallText className='font-light pb-2'>Phone Number</SmallText>
      <SmallText className='text-gray-200 font-light'>
        <a
          href='tel:+919266683150'
          className='hover:underline'
          onClick={() => track(ANALYTICS_EVENTS.PHONE_CLICKED, { surface: 'footer' })}
        >
          +91 9266683150
        </a>
      </SmallText>
    </div>
    <div className='flex flex-col'>
      <SmallText className='font-light pb-2'>Email</SmallText>
      <SmallText className='text-gray-200 font-light'>hello@xtractpro.in</SmallText>
    </div>
    <div className='flex flex-col'>
      <SmallText className='font-light pb-2'>Address</SmallText>
      <SmallText className='text-gray-200 font-light'>9-F, Moulsari Avenue,</SmallText>
      <SmallText className='text-gray-200 font-light'>DLF Phase 3, Sector 24,</SmallText>
      <SmallText className='text-gray-200 font-light'>GURUGRAM Haryana-122002</SmallText>
    </div>
    <div className='flex flex-col'>
      <SmallText className='font-light pb-2'>CIN</SmallText>
      <SmallText className='text-gray-200 font-light'>U28999DL2022PTC405185</SmallText>
    </div>
  </div>
)

const NavLinks = () => (
  // Five sections don't fit on one line until ~1500px, so wrap them into an
  // aligned grid rather than overflowing the footer card.
  <div className='hidden lg:grid lg:grid-cols-3 2xl:grid-cols-5 gap-x-10 gap-y-8'>
    {sectionFooterLinks.map((section) => (
      <div key={section.sectionName} className='flex flex-col'>
        <SmallText className='font-light pb-2'>{section.sectionName}</SmallText>
        <div className='flex flex-col gap-2'>
          {section.links.map((link) => (
            <Link key={link.to} to={link.to}>
              <SmallText className='font-light text-gray-200 hover:text-gray-400'>
                {link.label}
              </SmallText>
            </Link>
          ))}
        </div>
      </div>
    ))}
  </div>
)

export const Footer = () => {
  const navigate = useNavigate()

  const accordionItems = sectionFooterLinks.map((section) => ({
    id: section.sectionName,
    title: section.sectionName,
    content: (
      <div className='flex flex-col gap-2'>
        {section.links.map((link) => (
          <Link key={link.to} to={link.to}>
            <SmallText className='font-light text-gray-200 hover:text-gray-400'>
              {link.label}
            </SmallText>
          </Link>
        ))}
      </div>
    ),
  }))

  return (
    <div className='flex flex-col gap-4 mb-4'>
      {/* CTA Banner */}
      <div className='bg-dark-1 mx-4 md:mx-24 px-4 md:px-16 pb-8 pt-2 md:py-8 rounded-lg flex flex-col gap-8'>
        <div className='flex flex-col lg:flex-row justify-between gap-4'>
          <div className='flex flex-row items-end'>
            <H2 className='text-white'>Get in Touch With Us</H2>
            <p className='text-my-orange text-6xl'>.</p>
          </div>
          <Button
            variant='primary'
            className='w-1/2 lg:w-1/5'
            onClick={() => {
              track(ANALYTICS_EVENTS.FOOTER_CTA_CLICKED, { cta: 'enquire_now', destination: '/contact' })
              navigate('/contact')
            }}
          >
            Enquire Now&nbsp;&rarr;
          </Button>
        </div>

        <div className='border-t border-gray-700' />

        <NewsletterSignup />
      </div>

      {/* Main Footer */}
      <div className='bg-dark-1 mx-4 md:mx-24 px-4 md:px-16 py-8 rounded-lg flex flex-col lg:flex-row gap-8 lg:gap-16 items-start'>
        <div className='flex flex-col gap-8 w-full md:w-fit'>
          <img src={icon} alt='Xtractpro Logo' className='h-12 md:self-start' />

          {/* Desktop: socials, contact, nav links */}
          <div className='hidden lg:flex flex-row gap-10 xl:gap-16'>
            <div className='flex flex-col gap-4'>
              <SocialLinks />
              <ContactInfo />
            </div>
            <NavLinks />
          </div>
          <img src={startupIndia} alt='Startup India' className='h-12 hidden lg:block self-start' />
        </div>

        {/* Mobile: accordion nav */}
        <Accordion items={accordionItems} />

        {/* Mobile: socials, contact, startup india */}
        <div className='flex flex-col gap-4 lg:hidden w-full'>
          <SocialLinks />
          <ContactInfo />
          <div className='flex justify-center'>
            <img src={startupIndia} alt='Startup India' className='h-12' />
          </div>
        </div>
      </div>
    </div>
  )
}